import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import { FilterList, Clear } from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Property, PropertyType, Role } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useProperties } from '../../hooks/useApiCache';
import AddPropertyDialog from '../Properties/AddPropertyDialog';
import { LEBANON_GOVERNORATES, DISTRICT_MAP, transactionTypes, paymentMethods } from '../Properties/PropertyFormFields/propertyFormConstants';

// Fix Leaflet marker icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const LEBANON_CENTER: L.LatLngExpression = [33.8547, 35.8623];
const DEFAULT_ZOOM = 8;
const MIN_ZOOM = 6;
const MAX_ZOOM = 19;

// Validate latitude and longitude
const isValidCoordinate = (lat: number, lng: number): boolean => {
  return typeof lat === 'number' && 
         typeof lng === 'number' && 
         lat >= -90 && 
         lat <= 90 && 
         lng >= -180 && 
         lng <= 180;
};

const normalizeEnumLikeValue = (value?: string): string =>
  (value || '').toUpperCase().replace(/[\s-]/g, '_');

const MapView: React.FC = () => {
  const { user } = useAuth();
  console.log('[MapView] Mounted, user:', user?.email || 'NOT LOGGED IN');
  
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersGroupRef = useRef<L.FeatureGroup | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | undefined>();
  const [mapReady, setMapReady] = useState(false);
  const [governorateFilter, setGovernorateFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [townFilter, setTownFilter] = useState('');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  
  // Use React Query for properties data
  const { data: fetchedProperties, refetch } = useProperties();

  // District options based on selected governorate
  const districtOptions = useMemo(
    () => (governorateFilter ? DISTRICT_MAP[governorateFilter] || [] : []),
    [governorateFilter]
  );

  // Town options extracted from fetched properties matching current governorate + district
  const townOptions = useMemo(() => {
    if (!fetchedProperties) return [];
    const towns = fetchedProperties
      .filter((p: Property) => {
        if (!p.town) return false;
        if (governorateFilter && p.governorate !== governorateFilter) return false;
        if (districtFilter && p.district !== districtFilter) return false;
        return true;
      })
      .map((p: Property) => p.town as string);
    return Array.from(new Set(towns)).sort();
  }, [fetchedProperties, governorateFilter, districtFilter]);

  // Filter and validate properties based on user role
  const properties = useMemo(() => {
    if (!fetchedProperties) {
      console.log('[MapView] 🔴 No properties from cache yet');
      return [];
    }
    
    console.log('[MapView] ✅ Got properties from cache:', fetchedProperties.length);
    
    const isSuperAdmin = user?.role === Role.SUPER_ADMIN;

    // Filter properties based on user role
    const validProperties = fetchedProperties.filter((p: Property) => {
      // Check coordinate validity first
      const isValid = isValidCoordinate(p.latitude, p.longitude);
      if (!isValid) {
        console.warn('[MapView] Invalid coordinates for property:', p.id, p.title, { lat: p.latitude, lng: p.longitude });
        return false;
      }

      // Super admin can see all properties
      if (!isSuperAdmin && p.status !== 'APPROVED') return false;

      // Apply governorate / district / town filters
      if (governorateFilter && p.governorate !== governorateFilter) return false;
      if (districtFilter && p.district !== districtFilter) return false;
      if (townFilter && p.town !== townFilter) return false;

      // Apply property type / transaction type / payment method filters
      if (propertyTypeFilter && p.propertyType !== propertyTypeFilter) return false;
      if (transactionTypeFilter && p.transactionType !== transactionTypeFilter) return false;
      if (paymentMethodFilter && normalizeEnumLikeValue(p.paymentMethod) !== normalizeEnumLikeValue(paymentMethodFilter)) return false;

      return true;
    });

    console.log('[MapView] 📍 Displaying', validProperties.length, 'properties on map');
    return validProperties;
  }, [fetchedProperties, user?.role, governorateFilter, districtFilter, townFilter, propertyTypeFilter, transactionTypeFilter, paymentMethodFilter]);

  // Handle page visibility changes - only refetch if hidden for more than 30 seconds
  useEffect(() => {
    let hiddenTime: number | null = null;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenTime = Date.now();
      } else if (document.visibilityState === 'visible' && hiddenTime) {
        const hiddenDuration = Date.now() - hiddenTime;
        // Only refetch if hidden for more than 30 seconds (user came back from another tab/app)
        if (hiddenDuration > 30000) {
          console.log('[MapView] 🔄 Refetching after long absence');
          refetch();
        }
        hiddenTime = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetch]);

  const handleMapClick = (latlng: L.LatLng) => {
    setSelectedLocation({ lat: latlng.lat, lng: latlng.lng });
    setAddDialogOpen(true);
  };

  // Initialize map once on component mount - optimized for speed
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Use requestAnimationFrame for immediate initialization
    const initMap = () => {
      try {
        console.log('[MapView] 🗺️  Initializing map...');
        
        // Create map instance with best practices
        const mapInstance = L.map(mapRef.current!, {
          center: LEBANON_CENTER,
          zoom: DEFAULT_ZOOM,
          minZoom: MIN_ZOOM,
          maxZoom: MAX_ZOOM,
          scrollWheelZoom: true,
          dragging: true,
          doubleClickZoom: true,
          zoomControl: true,
          preferCanvas: true, // Use canvas for better performance
          zoomAnimation: true,
          fadeAnimation: true,
          markerZoomAnimation: true,
        });

        // Add multiple basemap layers (OSM, Esri Satellite, Carto Positron, Stamen Terrain)
        const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: MAX_ZOOM,
          maxNativeZoom: 18,
          updateWhenIdle: false,
          updateWhenZooming: false,
          keepBuffer: 2,
        });

        const esri = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
          attribution: 'Tiles © Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
          maxZoom: 19,
        });

        const positron = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap, &copy; CartoDB',
          maxZoom: 19,
        });

        const stamen = L.tileLayer('https://stamen-tiles-{s}.a.ssl.fastly.net/terrain/{z}/{x}/{y}.jpg', {
          attribution: 'Map tiles by Stamen Design, under CC BY 3.0. Data by OpenStreetMap, under ODbL.',
          maxZoom: 18,
        });

        // Default to OSM
        osm.addTo(mapInstance);

        // Add layer control for switching basemaps
        const baseMaps = {
          'OpenStreetMap': osm,
          'Satellite (Esri)': esri,
          'Carto Positron': positron,
          'Terrain (Stamen)': stamen,
        };
        L.control.layers(baseMaps, undefined, { position: 'topright', collapsed: false }).addTo(mapInstance);

        // Create feature group for markers
        markersGroupRef.current = L.featureGroup().addTo(mapInstance);

        // Add click listener to add properties
        mapInstance.on('click', (e: L.LeafletMouseEvent) => {
          handleMapClick(e.latlng);
        });

        mapInstanceRef.current = mapInstance;

        // Don't fit bounds initially - let markers do it
        console.log('[MapView] ✅ Map initialized and ready');
        setMapReady(true);
      } catch (error) {
        console.error('[MapView] ❌ Error initializing map:', error);
      }
    };

    // Initialize immediately on next frame
    const rafId = requestAnimationFrame(initMap);

    // Cleanup on unmount
    return () => {
      cancelAnimationFrame(rafId);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markersGroupRef.current = null;
        setMapReady(false);
      }
    };
  }, []);

  // Update markers when properties change OR when map becomes ready
  useEffect(() => {
    if (!mapInstanceRef.current || !markersGroupRef.current || !mapReady) {
      console.log('[MapView] ⏳ Waiting for map to be ready...');
      return;
    }

    console.log('[MapView] 🗺️  Adding', properties.length, 'markers to map...');

    // Clear existing markers
    markersGroupRef.current.clearLayers();

    // Add markers for each property
    const markerBounds = L.latLngBounds([]);
    let hasValidMarkers = false;
    const isSuperAdmin = user?.role === Role.SUPER_ADMIN;

    properties.forEach((property) => {
      // Double-check coordinate validity
      if (!isValidCoordinate(property.latitude, property.longitude)) {
        console.warn(`Invalid coordinates for property ${property.id}:`, {
          lat: property.latitude,
          lng: property.longitude,
        });
        return;
      }

      try {
        // Create custom marker with property type color
        const marker = L.marker(
          [property.latitude, property.longitude],
          {
            title: property.title,
            opacity: 0.9,
          }
        ).bindPopup(
          L.popup({
            maxWidth: 300,
            className: 'property-popup',
          }).setContent(`
            <div style="padding: 8px;">
              <h3 style="margin: 0 0 8px 0; font-size: 15px; font-weight: 600;">${property.title}</h3>
              <p style="margin: 4px 0; color: #666; font-size: 13px;">
                <strong>📍</strong> ${property.address}, ${property.city}
              </p>
              <p style="margin: 4px 0; color: #666; font-size: 13px;">
                <strong>🏠</strong> ${property.propertyType}
              </p>
              <p style="margin: 4px 0; color: #d32f2f; font-size: 14px; font-weight: 600;">
                💰 $${property.price.toLocaleString()}
              </p>
              ${property.bedrooms ? `<p style="margin: 4px 0; font-size: 13px;"><strong>🛏️</strong> ${property.bedrooms} bedroom${property.bedrooms !== 1 ? 's' : ''}</p>` : ''}
              ${property.size ? `<p style="margin: 4px 0; font-size: 13px;"><strong>📐</strong> ${property.size} m²</p>` : ''}
              ${property.agent ? `<p style="margin: 4px 0; color: #1976d2; font-size: 13px;"><strong>👤</strong> ${property.agent.user?.firstName} ${property.agent.user?.lastName}</p>` : ''}
              ${property.ownerName ? `<p style="margin: 4px 0; font-size: 13px;"><strong>Owner:</strong> ${property.ownerName}${property.ownerPhone ? ` • ${property.ownerPhone}` : ''}</p>` : ''}
              ${property.governorate ? `<p style="margin: 4px 0; font-size: 13px;"><strong>📍</strong> ${property.governorate}${property.district ? `, ${property.district}` : ''}${property.town ? ` • ${property.town}` : ''}</p>` : ''}
              ${isSuperAdmin ? `<p style="margin: 4px 0; font-size: 13px; padding: 4px; background: ${property.status === 'APPROVED' ? '#4caf50' : property.status === 'PENDING' ? '#ff9800' : '#f44336'}; color: white; border-radius: 3px;"><strong>Status:</strong> ${property.status}</p>` : ''}
              <p style="margin: 8px 0 4px 0; font-size: 12px; color: #999;">
                Lat: ${property.latitude.toFixed(6)} | Lng: ${property.longitude.toFixed(6)}
              </p>
              <a 
                href="${property.googleMapsLink ? property.googleMapsLink : `https://www.google.com/maps?q=${property.latitude},${property.longitude}`}" 
                target="_blank" 
                rel="noopener noreferrer"
                style="display: inline-block; margin-top: 8px; padding: 6px 12px; background: #1976d2; color: white; text-decoration: none; border-radius: 4px; font-size: 12px; font-weight: 500; text-align: center;"
              >
                🗺️ Open in Google Maps
              </a>
            </div>
          `)
        );

        marker.addTo(markersGroupRef.current!);
        markerBounds.extend([property.latitude, property.longitude]);
        hasValidMarkers = true;
      } catch (error) {
        console.error(`[MapView] ❌ Error creating marker for property ${property.id}:`, error);
      }
    });

    console.log('[MapView] ✅ Successfully added', hasValidMarkers ? properties.length : 0, 'markers to map');

    // Fit map bounds to show all markers - optimized
    if (hasValidMarkers && mapInstanceRef.current) {
      try {
        console.log('[MapView] 🎯 Fitting map to show all', properties.length, 'properties');
        // Use immediate fit without animation for faster display
        mapInstanceRef.current.fitBounds(markerBounds, {
          padding: [50, 50],
          maxZoom: 16,
          animate: false, // Disable animation for instant fit
          duration: 0,
        });
      } catch (error) {
        console.warn('[MapView] ⚠️  Could not fit bounds:', error);
      }
    } else if (mapInstanceRef.current && !hasValidMarkers) {
      // Set default view if no markers
      mapInstanceRef.current.setView(LEBANON_CENTER, DEFAULT_ZOOM, { animate: false });
      console.log('[MapView] 🗺️  No markers to display, using default view');
    }
  }, [properties, user?.role, mapReady]);

  const handlePropertyAdded = async (newProperty?: Property) => {
    if (newProperty) {
      console.log('[MapView] ✅ Property added successfully:', newProperty.title);
      // React Query handles the cache update automatically
    }
    setAddDialogOpen(false);
    setSelectedLocation(undefined);
  };

  return (
    <>
      {/* Filter Bar */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          borderRadius: 2,
          border: '1px solid rgba(16,24,40,0.06)',
          boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
        }}
      >
        <FilterList sx={{ color: 'text.secondary' }} />

        {/* Governorate */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Governorate</InputLabel>
          <Select
            value={governorateFilter}
            label="Governorate"
            onChange={(e) => {
              setGovernorateFilter(e.target.value);
              setDistrictFilter('');
              setTownFilter('');
            }}
          >
            <MenuItem value=""><em>All Governorates</em></MenuItem>
            {LEBANON_GOVERNORATES.map((g) => (
              <MenuItem key={g} value={g}>{g}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* District */}
        <FormControl size="small" sx={{ minWidth: 180 }} disabled={!governorateFilter}>
          <InputLabel>District</InputLabel>
          <Select
            value={districtFilter}
            label="District"
            onChange={(e) => {
              setDistrictFilter(e.target.value);
              setTownFilter('');
            }}
          >
            <MenuItem value=""><em>All Districts</em></MenuItem>
            {districtOptions.map((d) => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Town */}
        <FormControl size="small" sx={{ minWidth: 180 }} disabled={townOptions.length === 0}>
          <InputLabel>Town</InputLabel>
          <Select
            value={townFilter}
            label="Town"
            onChange={(e) => setTownFilter(e.target.value)}
          >
            <MenuItem value=""><em>All Towns</em></MenuItem>
            {townOptions.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Property Type */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Property Type</InputLabel>
          <Select
            value={propertyTypeFilter}
            label="Property Type"
            onChange={(e) => setPropertyTypeFilter(e.target.value)}
          >
            <MenuItem value=""><em>All Types</em></MenuItem>
            {Object.values(PropertyType).map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Transaction Type */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Listing Type</InputLabel>
          <Select
            value={transactionTypeFilter}
            label="Listing Type"
            onChange={(e) => setTransactionTypeFilter(e.target.value)}
          >
            <MenuItem value=""><em>All</em></MenuItem>
            {transactionTypes.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Payment Method */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Payment Method</InputLabel>
          <Select
            value={paymentMethodFilter}
            label="Payment Method"
            onChange={(e) => setPaymentMethodFilter(e.target.value)}
          >
            <MenuItem value=""><em>All</em></MenuItem>
            {paymentMethods.map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Clear filters */}
        {(governorateFilter || districtFilter || townFilter || propertyTypeFilter || transactionTypeFilter || paymentMethodFilter) && (
          <Tooltip title="Clear filters">
            <IconButton
              size="small"
              onClick={() => {
                setGovernorateFilter('');
                setDistrictFilter('');
                setTownFilter('');
                setPropertyTypeFilter('');
                setTransactionTypeFilter('');
                setPaymentMethodFilter('');
              }}
              sx={{ color: '#f44336' }}
            >
              <Clear />
            </IconButton>
          </Tooltip>
        )}

        {/* Property count */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {properties.length} {properties.length === 1 ? 'property' : 'properties'} shown
              </Typography>
            }
            size="small"
            sx={{
              bgcolor: properties.length === 0 ? '#fce4ec' : '#e3f2fd',
              color: properties.length === 0 ? '#c62828' : '#1565c0',
              fontWeight: 600,
              border: 'none',
            }}
          />
        </Box>
      </Paper>

      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: '600px',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          '& .leaflet-container': {
            height: '100%',
            width: '100%',
            borderRadius: 2,
            background: '#f0f0f0',
          },
          '& .leaflet-control-zoom': {
            borderRadius: '4px',
          },
          '& .property-popup': {
            '& .leaflet-popup-content-wrapper': {
              borderRadius: '6px',
              boxShadow: '0 3px 12px rgba(0, 0, 0, 0.15)',
            },
          },
        }}
      />
      <AddPropertyDialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setSelectedLocation(undefined);
        }}
        onSuccess={handlePropertyAdded}
        initialLocation={selectedLocation}
      />
    </>
  );
};

export default MapView;
